import React from 'react';
import { Link } from 'react-router-dom';
import { useSettings } from '../../contexts/SettingsContext';
import { 
  Facebook, 
  Twitter, 
  Instagram, 
  Mail, 
  Phone, 
  MapPin, 
  GraduationCap, 
  ChevronLeft,
  Youtube,
  ShieldCheck,
  Globe
} from 'lucide-react';

const Footer = () => {
  const { getSetting, getPlatformName, getSocialLinks, getSupportEmail } = useSettings();
  const currentYear = new Date().getFullYear();
  
  // Get settings from centralized database
  const platformName = getPlatformName();
  const platformDescription = getSetting('platform_description') || 'نحن نؤمن بأن التعليم هو مفتاح المستقبل. منصتنا توفر لك أفضل الخبرات التعليمية للوصول إلى القمة في الثانوية العامة.';
  const socialLinks = getSocialLinks();
  const supportEmail = getSupportEmail() || 'support@almanassa.com';
  const officePhone = getSetting('general_contact_phone') || '01012345678';
  const officeAddress = getSetting('office_address') || 'مصر';

  return (
    <footer className="relative bg-gray-900 text-gray-300 pt-12 pb-6 overflow-hidden rounded-t-[2rem] lg:rounded-t-[3rem]">
      {/* Decorative Background Elements */}
      <div className="absolute top-0 left-0 w-48 h-48 bg-primary-600/10 rounded-full blur-[80px] -z-0"></div>
      <div className="absolute bottom-0 right-0 w-64 h-64 bg-blue-600/10 rounded-full blur-[100px] -z-0"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Main Footer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          
          {/* Column 1: Brand Identity */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-900/20">
                <GraduationCap className="text-white w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-black text-white tracking-tight leading-none">{platformName}</h3>
                <span className="text-[9px] font-bold text-primary-400 uppercase tracking-widest mt-1 block">الجيل الذكي للتعلم</span>
              </div>
            </div>
            
            <p className="text-xs leading-relaxed text-gray-400 font-medium max-w-xs">
              {platformDescription}
            </p>

            <div className="flex items-center gap-2">
              {[
               { icon: Facebook, color: 'hover:bg-blue-600', key: 'facebook', url: socialLinks?.facebook || 'https://www.facebook.com/munasatiofficial' },
               { icon: Twitter, color: 'hover:bg-sky-500', key: 'twitter', url: socialLinks?.twitter || 'https://twitter.com/almanassa' },
               { icon: Instagram, color: 'hover:bg-pink-600', key: 'instagram', url: socialLinks?.instagram || 'https://instagram.com/almanassa' },
               { icon: Youtube, color: 'hover:bg-red-600', key: 'youtube', url: socialLinks?.youtube || 'https://youtube.com/almanassa' }
              ].map((social) => (
                <a
                  key={social.key}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center transition-all duration-300 group ${social.color}`}
                >
                  <social.icon className="w-4 h-4 group-hover:text-white transition-colors" />
                </a>
              ))}
            </div>
          </div>

          {/* Column 2: Educational Links */}
          <div>
            <h4 className="text-white font-black text-base mb-6 flex items-center gap-2 uppercase tracking-tighter">
                <div className="w-1 h-3 bg-primary-600 rounded-full"></div>
                روابط تعليمية
            </h4>
            <ul className="space-y-4">
              {[
                { name: 'استكشاف الكورسات', path: '/student/browse' },
                { name: 'نخبة المدرسين', path: '/teachers' },
                { name: 'لوحة التحكم', path: '/dashboard' },
                { name: 'عن المنصة', path: '/about' }
              ].map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.path} 
                    className="flex items-center gap-2 text-sm font-bold text-gray-400 hover:text-white hover:translate-x-[-8px] transition-all duration-300 group"
                  >
                    <ChevronLeft size={14} className="text-primary-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Support & Legal */}
          <div>
            <h4 className="text-white font-black text-base mb-6 flex items-center gap-2 uppercase tracking-tighter">
                <div className="w-1 h-3 bg-primary-600 rounded-full"></div>
                الدعم والمساعدة
            </h4>
            <ul className="space-y-3">
              {[
                { name: 'مركز المساعدة', path: '/help' },
                { name: 'سياسة الخصوصية', path: '/privacy' },
                { name: 'الشروط والأحكام', path: '/terms' },
                { name: 'تواصل معنا', path: '/contact' }
              ].map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.path} 
                    className="flex items-center gap-2 text-xs font-bold text-gray-400 hover:text-white hover:translate-x-[-4px] transition-all duration-300 group"
                  >
                    <ChevronLeft size={12} className="text-primary-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Contact Info */}
          <div>
            <h4 className="text-white font-black text-base mb-6 flex items-center gap-2 uppercase tracking-tighter">
                <div className="w-1 h-3 bg-primary-600 rounded-full"></div>
                تواصل مباشر
            </h4>
            <div className="space-y-4">
              <div className="group flex items-start gap-3">
                <div className="w-8 h-8 bg-primary-600/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-primary-600 transition-colors">
                    <Phone className="w-3 h-3 text-primary-500 group-hover:text-white" />
                </div>
                <div>
                    <p className="text-[9px] font-black text-gray-500 uppercase">اتصل بنا</p>
                    <a href={`tel:+${officePhone.replace(/[^\d]/g, '')}`} className="text-xs font-black text-white hover:text-primary-400 transition-colors tracking-wide">
                       {officePhone}
                    </a>
                </div>
              </div>

              <div className="group flex items-start gap-3">
                <div className="w-8 h-8 bg-primary-600/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-primary-600 transition-colors">
                    <Mail className="w-3 h-3 text-primary-500 group-hover:text-white" />
                </div>
                <div>
                    <p className="text-[9px] font-black text-gray-500 uppercase">البريد الإلكتروني</p>
                    <a href={`mailto:${supportEmail}`} className="text-xs font-black text-white hover:text-primary-400 transition-colors break-all">
                        {supportEmail}
                    </a>
                </div>
              </div>

              <div className="group flex items-start gap-3">
                <div className="w-8 h-8 bg-primary-600/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-primary-600 transition-colors">
                    <MapPin className="w-3 h-3 text-primary-500 group-hover:text-white" />
                </div>
                <div>
                    <p className="text-[9px] font-black text-gray-500 uppercase">الموقع</p>
                    <p className="text-xs font-bold text-gray-400 group-hover:text-white transition-colors">
                        {officeAddress}
                    </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar: Copyright & Payment Methods */}
        <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex flex-col md:flex-row items-center gap-3 text-center md:text-right">
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">
              &copy; {currentYear} {platformName}. جميع الحقوق محفوظة
            </p>
            <div className="hidden md:block w-0.5 h-0.5 bg-gray-700 rounded-full"></div>
            <p className="text-[9px] font-medium text-gray-600 italic">
                صُمم بكل حب لدعم رحلة نجاح الطلاب.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            <div className="flex items-center gap-1.5 text-[9px] font-black text-gray-500 uppercase bg-white/5 px-3 py-1.5 rounded-lg border border-white/5">
                <ShieldCheck size={12} className="text-emerald-500" /> دفع آمن 100٪
            </div>
            <div className="flex items-center gap-1.5 text-[9px] font-black text-gray-500 uppercase bg-white/5 px-3 py-1.5 rounded-lg border border-white/5">
                <Globe size={12} className="text-primary-500" /> اللغة: العربية
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Blur Circle */}
      <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-primary-600/20 rounded-full blur-[60px]"></div>
    </footer>
  );
};

export default Footer;